// Receiver.js version 2.01.20210702

import Controller from './Controller.js';
import string_table from './StringTable.js';
import { language_list } from './config/Language_List.js';
import { broadcastMessage } from './Broadcast.js';

//For ecnryption
const NodeRSA = require('node-rsa');

/*
 * Error Messages
 * 
 * {'id': integer, severity: string(Severity), 'message': string(Error Message), 'name': string(Controller Name)}
 * 
 * Severity(Fatal, Severe, Low)
 * Fatal will cause App to fail
 * Severe will cause some app components to fail
 * Low can be ignored 
 * 
 * General Errors
 * id = 101 message = Unable to add controller. Controller Name is empty.
 * id = 102 message = "Something went wrong while adding controller: " + error message
 * id = 103 message = Wrong Account Name or Password.
 * id = 104 message = No active controller
 * id = 105 message = Log in is blocked for 3sec because login failed.
 * 
 * App Side Error
 * id = 201 message = RSA Key pair does not exist. Use getRSAKeyPair to retrieve key pair.
 * id = 202 message = RSA Key pair cannot be created.
 * 
 * Controller Side Errors
 * (NOT USED) id = 501 message = Unable to establish connection, Create websocket fail.
 * (NOT USED) id = 502 message = Unable to establish connection to SSC, Trying local connection.
 * (NOT USED) id = 503 message = There is an error to access SVM system.
 * (NOT USED) id = 504 message = Maximum connection retries reached.
 * (NOT USED) id = 505 message = Login Timeout.
 * id = 506 message = Websocket closed abnormally or unexpectedly.
 * id = 507 message = Connection to SSC established but controller not connected.
 * id = 508 message = Controller disconnected
 * (NOT USED) id = 509 message = Unable to establish local connection.
 * 
 * Websocket Errors
 * id = 601 message = Websocket Timeout.
 * (NOT USED) id = 602 message = Websocket Connection Failed.
 */

export default class Receiver {
	constructor() {
		// controller_list is an object containing all controller objects
		// key is the name of the controller
		this.controller_list = {};
		this.active_controller = null;
		// Lanuguage setting for the app.
		this.lang = 'EN';
		// For reconnections
		this.pending_login = false;
		this.pending_login_user = {};

		// Check if web app or mobile app
		//this.is_mobile = window.location.protocol.toString() == 'http:' || window.location.protocol == 'https:' ? false : true;
		this.is_mobile = false;

		if(window.hasOwnProperty("cordova")){
			this.is_mobile = true;
		}
	}

	getRSAKeyPair() {
		/*
		 * For RSA Encrytion
		 */
		try {
			const key = new NodeRSA();
			// create RSA private and public key
			key.generateKeyPair();
			this.public_key = key.exportKey('pkcs1-public-pem');
			this.private_key = key.exportKey('pkcs1-private-pem');
			// to create keys take long time so it should store in App and use stored keys
			this.common_key = '';
		} catch (e) {
			let error = { 'id': 202, severity: 'severe', 'message': "RSA Key pair cannot be created.", 'name': '' }
			this.error(error);
		}
	}

	/*
	 * Utility
	 */

	findController() {
		broadcastMessage(this);
	}

	// set new controller.
	setController(controller_info) {
		if (this.public_key == null || this.private_key == null) {
			this.getRSAKeyPair();
		}
		// Destroys the current active controller and creates a new controller.
		// Note: whenever switch controller, use the set controller function.
		this.active_controller = null;
		if (controller_info != null) {
			// Check that controller name exists
			if (controller_info.name == null || controller_info.name == undefined || controller_info.name == '') {
				let error = { 'id': 101, severity: 'severe', 'message': "Unable to add controller. Controller Name is empty.", 'name': '' }
				this.error(error);
				return false;
			}
			try {
				this.active_controller = new Controller(controller_info, this);
				this.controller_list[controller_info.name] = this.active_controller;
				return true;
			} catch (e) {
				console.log('error = ' + e.message);
				let error = { 'id': 102, severity: 'severe', 'message': "Something went wrong while adding controller: " + e.message, 'name': controller_info.name }
				this.error(error);
				return false;
			}
		}
	}

	onWSConnectionFailed() {
		// To handle case 12 situations eg. LAN Connection failed
		console.log('LAN Connection Failed')
	}

	/*
	 * API
	 */

	// Login command(called by app)
	login(user) {
		// Check if there's an active controller (i.e. selected controller)
		if (this.active_controller == null) {
			let error = { 'id': 104, severity: 'severe', 'message': "No active controller", 'name': '' }
			this.error(error);
			return false;
		}
		this.active_controller.login(user);
		this.pending_login_user = user;
	}

	// Logout command(called by app)
	logout() {
		this.active_controller.logout();
	}

	getLanguageList() {
		return language_list;
	}

	// Select display laungage. lang can be Eng,Brp,Esp,Thai,Viet,Baha,Ch,Hk,Tw,Jp
	setLanguage(language_setting) {
		this.lang = language_setting.lang;
	}

	getString(key) {
		if (key == 'DEG') {
			if (this.temp_unit == 'F') return '°F'
			else return '°C';
		}
		else {
			let word = string_table[this.lang][key];
			if (word == null) word = key;
			return word;
		}
	}

	getHwKey(mac) {
		let seed = mac.split(':');
		let add = ((parseInt(seed[0], 16) + parseInt(seed[5], 16)) % 0xff).toString(10);
		let addr1 = parseInt(seed[3] + seed[5] + seed[1], 16).toString(10);
		let addr2 = parseInt(seed[4] + seed[0] + seed[2], 16).toString(10);
		let addr = addr1 + add + addr2;
		return addr;
	}

	getPointList() {
		this.active_controller.getPointList();
	}

	reboot() {
		this.active_controller.reboot();
	}

	getGroups() {
		this.active_controller.getGroups();
	}

	addGroup(group_info) {
		this.active_controller.addGroup(group_info);
	}

	modifyGroup(group_info) {
		this.active_controller.modifyGroup(group_info);
	}

	deleteGroup(group_id) {
		this.active_controller.deleteGroup(group_id);
	}

	operate(command) {
		this.active_controller.operate(command);
	}

	exec(command) {
		this.active_controller.exec(command);
	}

	// Scene
	getScene() {
		this.active_controller.getScene();
	}

	addScene(scene_detail) {
		this.active_controller.addScene(scene_detail);
	}

	modifyScene(scene) {
		this.active_controller.modifyScene(scene);
	}

	deleteScene(scene_id) {
		this.active_controller.deleteScene(scene_id);
	}

	executeScene(scene_id) {
		this.active_controller.executeScene(scene_id);
	}

	// Schedule
	getSchedule() {
		this.active_controller.getSchedule();
	}

	addScheduleProgram(program) {
		this.active_controller.addScheduleProgram(program);
	}

	modifyScheduleProgram(program) {
		this.active_controller.modifyScheduleProgram(program);
	}

	enableScheduleProgram(program) {
		this.active_controller.enableScheduleProgram(program);
	}

	deleteScheduleProgram(program_id) {
		this.active_controller.deleteScheduleProgram(program_id);
	}

	addPattern(pattern) {
		this.active_controller.addPattern(pattern);
	}

	modifyPattern(pattern) {
		this.active_controller.modifyPattern(pattern);
	}

	deletePattern(pattern_id) {
		this.active_controller.deletePattern(pattern_id);
	}

	addCalendar(calendar) {
		this.active_controller.addCalendar(calendar);
	}

	modifyCalendar(calendar) {
		this.active_controller.modifyCalendar(calendar);
	}

	deleteCalendar(calendar_id) {
		this.active_controller.deleteCalendar(calendar_id);
	}

	// Interlock
	getInterlock() {
		this.active_controller.getInterlock();
	}

	addInterlockProgram(program) {
		this.active_controller.addInterlockProgram(program);
	}

	modifyInterlockProgram(program) {
		this.active_controller.modifyInterlockProgram(program);
	}

	enableInterlockProgram(program) {
		this.active_controller.enableInterlockProgram(program);
	}

	deleteInterlockProgram(program_id) {
		this.active_controller.deleteInterlockProgram(program_id);
	}

	loadCalcOption() {
		this.active_controller.loadCalcOption();
	}

	saveCalcOption(calc_option) {
		this.active_controller.saveCalcOption(calc_option);
	}

	saveHolidayCalendar(holiday_data) {
		this.active_controller.saveHolidayCalendar(holiday_data);
	}

	loadHolidayCalendar() {
		this.active_controller.loadHolidayCalendar();
	}

	getBillData(bill_data) {
		this.active_controller.getBillData(bill_data);
	}

	loadOwnerInfo() {
		this.active_controller.loadOwnerInfo();
	}

	saveOwnerInfo(owner_info) {
		this.active_controller.saveOwnerInfo(owner_info);
	}

	loadTenant() {
		this.active_controller.loadTenant();
	}

	saveTenant(tenant_info) {
		this.active_controller.saveTenant(tenant_info);
	}

	loadPPDPointList() {
		this.active_controller.loadPPDPointList();
	}

	loadPriceInfo() {
		this.active_controller.loadPriceInfo();
	}

	savePriceInfo(price_info) {
		this.active_controller.savePriceInfo(price_info);
	}

	getPPD(param) {
		this.active_controller.getPPD(param);
	}

	getOption() {
		this.active_controller.getOption();
	}

	getSSC() {
		this.active_controller.getSSC();
	}

	getAccess() {
		this.active_controller.getAccess();
	}

	getMailServer() {
		this.active_controller.getMailServer();
	}

	getNetwork() {
		this.active_controller.getNetwork();
	}

	getSSIDList() {
		this.active_controller.getSSIDList();
	}

	getDeviceConnection() {
		this.active_controller.getDeviceConnection();
	}

	getControllerList() {
		this.active_controller.getControllerList();
	}

	getAccounts() {
		this.active_controller.getAccounts();
	}

	getControllerInfo() {
		this.active_controller.getControllerInfo();
	}

	getTimeInfo() {
		this.active_controller.getTimeInfo();
	}

	getTimezones() {
		this.active_controller.getTimezones();
	}

	addAccount(account) {
		this.active_controller.addAccount(account);
	}

	modifyAccount(account) {
		this.active_controller.modifyAccount(account);
	}

	deleteAccount(name) {
		this.active_controller.deleteAccount(name);
	}

	setPassword(user_detail) {
		this.active_controller.setPassword(user_detail);
	}

	setPointSettings(point_settings) {
		this.active_controller.setPointSettings(point_settings);
	}

	setOption(option_settings) {
		this.active_controller.setOption(option_settings);
	}

	setSSC(ssc) {
		this.active_controller.setSSC(ssc);
	}

	setNTP(ntp) {
		this.active_controller.setNTP(ntp);
	}

	setTimezone(timezone) {
		this.active_controller.setTimezone(timezone);
	}

	setTime(time) {
		this.active_controller.setTime(time);
	}

	setAccess(access) {
		this.active_controller.setAccess(access);
	}

	setMailServer(mail_server_settings) {
		this.active_controller.setMailServer(mail_server_settings);
	}

	setNetwork(network_settings) {
		this.active_controller.setNetwork(network_settings);
	}

	setDeviceConnection(dev_con_settings) {
		this.active_controller.setDeviceConnection(dev_con_settings);
	}

	setPointList(point_list) {
		this.active_controller.setPointList(point_list);
	}

	setControllerInfo(controller_info_settings) {
		this.active_controller.setControllerInfo(controller_info_settings);
	}

	// Changeover
	getChangeOverGroups() {
		this.active_controller.getChangeOverGroups();
	}

	addChangeover(changeover_info) {
		this.active_controller.addChangeover(changeover_info);
	}

	modifyChangeover(changeover_info) {
		this.active_controller.modifyChangeover(changeover_info);
	}

	deleteChangeover(grp_id) {
		this.active_controller.deleteChangeover(grp_id);
	}

	enableChangeover(grp_id) {
		this.active_controller.enableChangeover(grp_id);
	}

	// History
	getHistory(search_date) {
		// search_date = {"year":year,"month":month,"day":day}
		this.active_controller.getHistory(search_date);
	}

	// Report Settings
	loadReportSettings() {
		this.active_controller.loadReportSettings();
	}

	saveReportSettings(report_settings) {
		this.active_controller.saveReportSettings(report_settings);
	}

	// Operation Info
	getOptimePointList() {
		this.active_controller.getOptimePointList();
	}

	getTotalOpTime(points) {
		// points = array of management points
		this.active_controller.getTotalOpTime(points);
	}

	// Not Used
	/*getHourlyOpTime(search_details){
		// search_details = {"year":year,"month":month,"day":day,"points":[point id]}
		this.active_controller.getHourlyOpTime(search_details);
	}*/

	getDailyOpTime(search_details) {
		// search_details = {"fy":year,"fm":month,"fd":day,"ty":year,"tm":month,"td":day,"points":[point id]}
		this.active_controller.getDailyOpTime(search_details);
	}

	getMonthlyOpTime(search_details) {
		// search_details = {"fy":year,"fm":month,"fd":day,"ty":year,"tm":month,"td":day,"points":[point id]}
		this.active_controller.getMonthlyOpTime(search_details);
	}

	// Error Info
	getDailyErrorInfo(search_details) {
		// eg. search_details = {"year":year,"month":month,"day":day}
		this.active_controller.getDailyErrorInfo(search_details);
	}

	getMonthlyErrorInfo(search_details) {
		// eg. search_details = {"fy":year,"fm":month,"fd":day,"ty":year,"tm":month,"td":day}
		this.active_controller.getMonthlyErrorInfo(search_details);
	}

	// Energy and Water Management
	getMeterPointList() {
		this.active_controller.getMeterPointList();
	}

	getHourlyMeter(search_details) {
		// eg. search_details = {"year":year,"month":month,"day":day,"points":[point id]}
		this.active_controller.getHourlyMeter(search_details);
	}

	getDailyMeter(search_details) {
		// eg. search_details = {"fy":year,"fm":month,"fd":day,"ty":year,"tm":month,"td":day,"points":[point id]}
		this.active_controller.getDailyMeter(search_details);
	}

	getMonthlyMeter(search_details) {
		// eg. search_details = {"fy":year,"fm":month,"fd":day,"ty":year,"tm":month,"td":day,"points":[point id]}
		this.active_controller.getMonthlyMeter(search_details);
	}

	// Trend Data
	getTrendPointList() {
		this.active_controller.getTrendPointList();
	}

	getTrend(search_details) {
		// eg. search_details = {"fy":year,"fm":month,"fd":day,"fh":hour,"fmin":min,"ty":year,"tm":month,"td":day,"th":hour,"tmin":min,"points":[[point id,type],…]}
		this.active_controller.getTrend(search_details);
	}

	// Realtime Energy
	getRealtimeEnergy() {
		this.active_controller.getRealtimeEnergy();
	}

	// JSON file read and save
	readJSONFile(file_details) {
		this.active_controller.readJSONFile(file_details);
	}

	saveJSONFile(file_details) {
		this.active_controller.saveJSONFile(file_details);
	}

	loadImage(image_details) {
		this.active_controller.loadImage(image_details);
	}

	saveImage(image_details) {
		this.active_controller.saveImage(image_details);
	}

	// Check Updates
	checkUpdates() {
		this.active_controller.checkUpdates();
	}

	updateController() {
		this.active_controller.updateController();
	}

	getZWDevices() {
		this.active_controller.getZWDevices();
	}

	zwHealNetwork() {
		this.active_controller.zwHealNetwork();
	}

	zwHardReset() {
		this.active_controller.zwHardReset();
	}

	zwPairingS0() {
		this.active_controller.zwPairingS0();
	}

	zwPairingS2() {
		this.active_controller.zwPairingS2();
	}

	zwStopPairing() {
		this.active_controller.zwStopPairing();
	}

	zwUnpair() {
		this.active_controller.zwUnpair();
	}

	zwRmFailedNode(failedNode) {
		this.active_controller.zwRmFailedNode(failedNode);
	}

	zwReplaceNode(failedNode) {
		this.active_controller.zwReplaceNode(failedNode);
	}

	getZWDevParam(nodeID) {
		this.active_controller.getZWDevParam(nodeID);
	}

	setZWDevParam(paramPayload) {
		this.active_controller.setZWDevParam(paramPayload);
	}

	// Hotel Guest Room
	getGuestRooms() {
		this.active_controller.getGuestRooms();
	}

	setRoomRental(rental_details) {
		// Eg. {unit name:"on"/"off",…}
		this.active_controller.setRoomRental(rental_details);
	}

	getControlParam() {
		this.active_controller.getControlParam();
	}

	setControlParam(ctrl_param) {
		this.active_controller.setControlParam(ctrl_param);
	}

	// MSM - Multi Site Management
	getMSMSiteList() {
		this.active_controller.getMSMSiteList();
	}

	getMSMSiteInfo(site_id) {
		this.active_controller.getMSMSiteInfo(site_id);
	}

	getMSMTempTargList(site_id) {
		this.active_controller.getMSMTempTargList(site_id);
	}

	getMSMValTargList(site_id) {
		this.active_controller.getMSMValTargList(site_id);
	}

	addMSMSite(site_info) {
		this.active_controller.addMSMSite(site_info);
	}

	modifyMSMSite(site_info) {
		this.active_controller.modifyMSMSite(site_info);
	}

	deleteMSMSite(site_id) {
		this.active_controller.deleteMSMSite(site_id);
	}

	/*
	 * Receive Events
	 */

	// On start up rceiver creates each controller and comm_module.
	// Comm_module will try to create a connection with the server.
	// When WS on success connection it will call rcvWSConnection
	rcvWSConnected() {
		console.log('Websocket connected')
	}

	// If login succeed then true, false means login failed. (called by controller)
	loginStatus(is_logged_in) {
		if (!is_logged_in) {
			console.log('unable to login')
		} else {
			console.log('logged in')
		}

		this.pending_login_user = {};
	}

	// Receive cos of management point
	cos(cos_info) {
		console.log('cos_info = ' + JSON.stringify(cos_info))
	}

	// Receive error information and message
	error(error) {
		console.log('error = ' + error.id + ': ' + error.message);
	}

	rcvTime(time_info) {
		console.log('time_info = ' + JSON.stringify(time_info))
	}

	// Receive all controllers in the list
	rcvFoundController(controller_info) {
		console.log('controller_info = ' + JSON.stringify(controller_info))
	}

	rcvSysInfo(sys_info) {
		if (this.pending_login) {
			this.login(this.pending_login_user);
		}
	}

	rcvPointList(point_list) {
		console.log('point_list = ' + JSON.stringify(point_list))
	}

	rcvGroups(groups) {
		console.log('groups = ' + JSON.stringify(groups));
	}

	rcvAddGroup(response) {
		console.log('response = ' + JSON.stringify(response))
	}

	rcvModifyGroup(response) {
		console.log('response = ' + JSON.stringify(response))
	}

	rcvDeleteGroup(response) {
		console.log('response = ' + JSON.stringify(response))
	}

	rcvScene(response) {
		console.log('response = ' + JSON.stringify(response))
	}

	rcvAddScene(response) {
		console.log('Add Scene Successful')
	}

	rcvModifyScene(response) {
		console.log('Modify Scene Successful')
	}

	rcvDeleteScene(response) {
		console.log('Modify Scene Successful');
	}

	rcvExecuteScene(response) {
		console.log('Execute Scene Successful');
	}

	rcvSchedule(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvAddScheduleProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvModifyScheduleProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvEnableScheduleProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDeleteScheduleProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvAddPattern(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvModifyPattern(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDeletePattern(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvAddCalendar(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvModifyCalendar(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDeleteCalendar(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvInterlock(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvAddInterlockProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvModifyInterlockProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvEnableInterlockProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDeleteInterlockProgram(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvOption(response) {
		console.log('Option response: ' + JSON.stringify(response));
	}

	rcvSetOption(response) {
		console.log('Set Option response: ' + JSON.stringify(response));
	}

	rcvSSC(response) {
		console.log('SSC response: ' + JSON.stringify(response));
	}

	rcvAccess(response) {
		console.log('Access response: ' + JSON.stringify(response));
	}

	rcvMailServer(response) {
		console.log('Mail Server response: ' + JSON.stringify(response));
	}

	rcvSetMailServer(response) {
		console.log('Set Mail Server response: ' + JSON.stringify(response));
	}

	rcvNetwork(response) {
		console.log('Network response: ' + JSON.stringify(response));
	}

	rcvSetNetwork(response) {
		console.log('Set Network response: ' + JSON.stringify(response));
	}

	rcvSSIDList(response) {
		console.log('SSID response: ' + JSON.stringify(response));
	}

	rcvDeviceConnection(response) {
		console.log('Device Connection response: ' + JSON.stringify(response));
	}

	rcvSetDeviceConnection(response) {
		console.log('Set Device Connection response: ' + JSON.stringify(response));
	}

	rcvSetPointList(response) {
		console.log('Set Point List response: ' + JSON.stringify(response));
	}

	rcvSetTime(response) {
		console.log('Set Time response: ' + JSON.stringify(response));
	}

	rcvControllerList(response) {
		console.log('Controller list response: ' + JSON.stringify(response));
	}

	rcvControllerInfo(response) {
		console.log('Controller Info response: ' + JSON.stringify(response));
	}

	rcvTimeInfo(response) {
		console.log('Time Info response: ' + JSON.stringify(response));
	}

	rcvTimezones(response) {
		console.log('Timezones response: ' + JSON.stringify(response));
	}

	rcvSetControllerInfo(response) {
		console.log('Set Controller Info response: ' + JSON.stringify(response));
	}

	rcvAccounts(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvAddAccount(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvModifyAccount(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDeleteAccount(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvChangePassword(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvSetPointSettings(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvExec(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvChangeoverGroups(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvAddChangeover(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvModifyChangeover(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDeleteChangeover(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvEnableChangeover(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvSetTime(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvHistory(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	// Not Used
	/*rcvHourlyOpTime(response){
		console.log('response = '+ JSON.stringify(response));
	}*/

	rcvOptimePointList(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvTotalOpTime(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDailyOpTime(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvMonthlyOpTime(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDailyErrorInfo(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvMonthlyErrorInfo(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvMeterPointList(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvHourlyMeter(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvDailyMeter(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvMonthlyMeter(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvTrendPointList(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvTrendData(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvRealtimeEnergy(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvRealtimeEnergyCos(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvLoadPPDPointList(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvGetPPD(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvReadJSONFile(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvSaveJSONFile(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvLoadImage(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvSaveImage(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvCheckUpdate(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvUpdateCommandSent(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvSetTimeDate(response) {
		console.log('response = ' + JSON.stringify(response));
	}

	rcvZWDevices(response) {
		console.log('rcvZWDevices response: ' + JSON.stringify(response));
	}

	rcvZWHealNetwork(response) {
		console.log('rcvZWHealNetwork response: ' + JSON.stringify(response));
	}

	rcvZWHardReset(response) {
		console.log('rcvZWHardReset response: ' + JSON.stringify(response));
	}

	rcvZWRmFailedNode(response) {
		console.log('rcvZWRmFailedNode response: ' + JSON.stringify(response));
	}

	rcvZWUnpair(response) {
		console.log('rcvZWUnpair response: ' + JSON.stringify(response));
	}

	rcvZWPairing(response) {
		console.log('rcvZWPairing response: ' + JSON.stringify(response));
	}

	rcvZWStopPairing(response) {
		console.log('rcvZWStopPairing response: ' + JSON.stringify(response));
	}

	rcvZWReplaceNode(response) {
		console.log('rcvZWReplaceNode response: ' + JSON.stringify(response));
	}

	rcvZWGetDevParam(response) {
		console.log('rcvZWGetDevParam response: ' + JSON.stringify(response));
	}

	rcvZWSetDevParam(response) {
		console.log('rcvZWSetDevParam response: ' + JSON.stringify(response));
	}

	rcvGuestRoomsData(response) {
		console.log('rcvGuestRoomsData response: ' + JSON.stringify(response));
	}

	rcvSetRoomRental(response) {
		console.log('rcvSetRoomRental response: ' + JSON.stringify(response));
	}

	rcvGetControlParam(response) {
		console.log('rcvSetRoomRental response: ' + JSON.stringify(response));
	}

	rcvSetControlParam(response) {
		console.log('rcvSetRoomRental response: ' + JSON.stringify(response));
	}

	rcvMSMSiteList(response) {
		console.log('rcvMSMSiteList response: ' + JSON.stringify(response));
	}

	rcvMSMSiteStat(response) {
		console.log('rcvMSMSiteList response: ' + JSON.stringify(response));
	}

	rcvMSMTempTargList(response) {
		console.log('rcvMSMTempTargList response: ' + JSON.stringify(response));
	}

	rcvMSMValTargList(response) {
		console.log('rcvMSMValTargList response: ' + JSON.stringify(response));
	}

	rcvMSMSiteInfo(response) {
		console.log('rcvMSMSiteInfo response: ' + JSON.stringify(response));
	}

	rcvMSMAddSite(response) {
		console.log('rcvMSMAddSite response: ' + JSON.stringify(response));
	}

	rcvMSMModifySite(response) {
		console.log('rcvMSMModifySite response: ' + JSON.stringify(response));
	}

	rcvMSMDeleteSite(response) {
		console.log('rcvMSMDeleteSite response: ' + JSON.stringify(response));
	}

	rcvLoadReportSettings(response) {
		console.log('rcvLoadReportSettings response: ' + JSON.stringify(response));
	}

	rcvSaveReportSettings(response) {
		console.log('rcvLoadReportSettings response: ' + JSON.stringify(response));
	}
}